import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { format, parseISO } from 'date-fns';
import { catchError, EMPTY, forkJoin, map, of, switchMap } from 'rxjs';
import { CountIntegrationByFilter } from 'src/app/modules/administration/integration-management/actions/integration-management-search.actions';
import { IntegrationManagementService } from 'src/app/modules/administration/integration-management/services/integration-management.service';
import { SearchLiteralsByFilterSuccess } from 'src/app/modules/administration/locale-management/actions/search-literals.actions';
import { LiteralsSearchService } from 'src/app/modules/administration/locale-management/services/search-literals.service';
import { UserSearchService } from 'src/app/modules/administration/user-management/services/search.service';
import { SearchFlowsService } from 'src/app/modules/automation-center/flows/services/search-flows.service';
import { RecipeGridSuccess } from 'src/app/modules/automation-center/recipes/actions/search.actions';
import { SearchRecipesService } from 'src/app/modules/automation-center/recipes/services/search.service';
import { CampaignExecutionsService } from 'src/app/modules/campaign-management/campaign-executions/services/search.service';
import { GetListsSuccess } from 'src/app/modules/campaign-management/campaigns/actions/lists.actions';
import { ListsService } from 'src/app/modules/campaign-management/campaigns/services/lists.service';
import { CountCheckbooksByFilter } from 'src/app/modules/campaign-management/checkbooks/actions/search-checkbooks.actions';
import { SearchCheckbooksService } from 'src/app/modules/campaign-management/checkbooks/services/search-checkbooks.service';
import { SearchGroupTemplatesService } from 'src/app/modules/campaign-management/checkbooks/services/search-group-templates.service';
import { ClubsSearchService } from 'src/app/modules/customer-data/clubs/services/clubs-search.service';
import { AllCustomersService } from 'src/app/modules/customer-data/customers/services/all-customers.service';
import { LoyaltyMembersService } from 'src/app/modules/customer-data/customers/services/members-search.service';
import { InvoicesSearchService } from 'src/app/modules/customer-data/invoices/services/invoices-search.service';
import { TicketsSearchService } from 'src/app/modules/customer-data/tickets/services/tickets-search.service';
import { BulkHistorySearchService } from 'src/app/modules/customer-operations/bulk-operations/services/bulk-history-search.service';
import { SearchBulkService } from 'src/app/modules/customer-operations/bulk-operations/services/search-bulk.service';
import { SearchBrandService } from 'src/app/modules/data-customization/brand-management/services/brand-search.service';
import { SearchTagsAssignmentService } from 'src/app/modules/data-customization/tag-management/services/search-tag-assignment.service';
import { SearchTagsService } from 'src/app/modules/data-customization/tag-management/services/search-tags.service';
import { SearchSchemasService } from 'src/app/modules/loyalty-model/schemas/services/search.service';
import { SearchRedeemRulesService } from 'src/app/modules/loyalty-model/redeem-rules/services/search.service';
import { MerchantsSearchService } from 'src/app/modules/merchant/services/search.service';
import { GridFailed, GridSuccess } from '../actions/grid.actions';
import { OpenSnackbar } from '../actions/notification.actions';
import { CountChanged } from '../actions/paginator.actions';
import { ActiveComponent, QcResponseGrid } from '../models/grid.model';
import { EliquaState } from '../reducers/eliqua.reducer';
import * as selectors from '../selectors/eliqua.selectors';
import { FacadeService } from './facade.service';
import { LiteralsFacadeService } from './literals.facade.service';
import { SystemLiteralsFacadeService } from './literals/system.literals.facade.service';
import { CampaignSearchService } from 'src/app/modules/campaign-management/campaigns/services/campaign-search.service';

@Injectable({
  providedIn: 'root',
})
export class EliquaFacadeService extends FacadeService {
  constructor(
    public override store: Store<EliquaState>,
    private searchTagsService: SearchTagsService,
    private searchTagsAssignmentService: SearchTagsAssignmentService,
    private searchBrandService: SearchBrandService,
    private literalsFacade: LiteralsFacadeService,
    private systemLiterals: SystemLiteralsFacadeService,
    private searchIntegrationManagement: IntegrationManagementService,
    private searchBulkService: SearchBulkService,
    private bulkHistory: BulkHistorySearchService,
    private userSearch: UserSearchService,
    private invoicesService: InvoicesSearchService,
    private searchCheckbooks: SearchCheckbooksService,
    private searchGroupTemplatesService: SearchGroupTemplatesService,
    private merchantSearchService: MerchantsSearchService,
    private clubSearchService: ClubsSearchService,
    private listsService: ListsService,
    private campaignService: CampaignExecutionsService,
    private literalsSearchService: LiteralsSearchService,
    private schemaService: SearchSchemasService,
    private ticketsService: TicketsSearchService,
    private flowsService: SearchFlowsService,
    private recipesService: SearchRecipesService,
    private lmService: LoyaltyMembersService,
    private acService: AllCustomersService,
    private redeemRulesService: SearchRedeemRulesService,
    private campaignSearchService: CampaignSearchService,
  ) {
    super(store);
  }

  activeComponent$ = this.storeSelect(selectors.getActiveComponent);
  activeTabIndex$ = this.storeSelect(selectors.getActiveTabIndex);
  isSubscriptionACtive$ = this.storeSelect(selectors.getSubscriptionValidity);
  enableLockedOverlay$ = this.storeSelect(selectors.enableLockedOverlay);
  subscriptionEndDate$ = this.storeSelect(selectors.getSubscriptionEndDate);
  renderPaginator$ = this.storeSelect(selectors.getRenderPaginator);
  language$ = this.storeSelect(selectors.getLanguage);

  get activeComponent() {
    return this.storeSnapshot(this.activeComponent$);
  }

  get activeTabIndex() {
    return this.storeSnapshot(this.activeTabIndex$);
  }

  get language() {
    return this.storeSnapshot(this.language$);
  }

  serviceSelect() {
    switch (this.activeComponent) {
      case ActiveComponent.SearchTags:
        return this.searchTagsService.searchTags().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.literalsFacade.literals.literals['liquid.tagmanagement.errormsg'],
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.TagAssignment:
        return this.searchTagsAssignmentService.searchTags().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.literalsFacade.literals.literals['liquid.tagmanagement.errormsg'],
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.SearchBrand:
        return this.searchBrandService.searchBrand().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.SearchIntegration:
        return this.searchIntegrationManagement.getIntegrationTypes().pipe(
          switchMap((res) => of(GridSuccess({ res }), CountIntegrationByFilter())),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.SearchBulk:
        return this.searchBulkService.searchBulk().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.BulkHistory:
        return this.bulkHistory.searchBulkHistory().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.literalsFacade.literals.literals['liquid.system.campaign.error'],
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.UserSearch:
        return this.userSearch.users().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.literalsFacade.literals.literals['liquid.system.campaign.error'],
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.Invoices:
        return this.invoicesService.invoicesSearch().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.literalsFacade.literals.literals['liquid.system.campaign.error'],
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.SearchCheckbooks:
        return this.searchCheckbooks.getCheckbooks().pipe(
          switchMap((res: any) => of(GridSuccess({ res }), CountCheckbooksByFilter())),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.MerchantSearch:
        return this.merchantSearchService.merchants().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.SearchGroupTemplates:
        return this.searchGroupTemplatesService.getGroupTemplates().pipe(
          switchMap((res: any) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.ClubSearch:
        return this.clubSearchService.search().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.literalsFacade.literals.literals['liquid.system.campaign.error'],
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.Lists:
        return this.listsService.getLists().pipe(
          switchMap((res: any) => of(GridSuccess({ res }), GetListsSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.CampaignExecutions:
        return this.campaignService.search().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError((error) => {
            return error.status === 404
              ? of(
                  GridSuccess({
                    res: {
                      messages: [{ type: 'error', description: 'No results found', code: 404 }],
                      payload: { data: [], filterResults: '' },
                      success: false,
                    },
                  }),
                )
              : of(
                  GridFailed(),
                  OpenSnackbar({
                    message: this.systemLiterals.systemGridFailed,
                    panelClass: 'failed-snackbar',
                  }),
                );
          }),
        );
      case ActiveComponent.LiteralsSearch:
        return this.literalsSearchService.literalsByFilter().pipe(
          switchMap((res: any) => of(SearchLiteralsByFilterSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.CardBatchesSearch:
        // Handled by CardBatchesEffects — return empty to prevent shared GridEffects from interfering
        return EMPTY;
      case ActiveComponent.Schemas:
        return forkJoin({
          search: this.schemaService.searchSchemas(),
          count: this.schemaService.countSchemas(),
        }).pipe(
          switchMap(({ search, count }: { search: any; count: number }) =>
            of(GridSuccess({ res: search }), CountChanged({ totalPages: count })),
          ),
          catchError((error) =>
            of(
              GridFailed(),
              OpenSnackbar({
                message:
                  error.status === 404 ? this.systemLiterals.noResultsFound : this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.Tickets:
        return this.ticketsService.ticketsSearch().pipe(
          switchMap((res) => of(GridSuccess({ res }))),
          catchError((error) =>
            of(
              GridFailed(),
              OpenSnackbar({
                message:
                  error.status === 404 ? this.systemLiterals.noResultsFound : this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.Flows:
        return this.flowsService.searchFlows().pipe(
          switchMap((res: any) => of(GridSuccess({ res }))),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.MemberSearch:
        return this.lmService.search().pipe(
          switchMap((res: any) => of(GridSuccess({ res }))),
          catchError((error) =>
            of(
              GridFailed(),
              OpenSnackbar({
                message:
                  error.status === 404 ? this.systemLiterals.noResultsFound : this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.AllCustomers:
        return this.acService.search().pipe(
          switchMap((res: any) => of(GridSuccess({ res }))),
          catchError((error) =>
            of(
              GridFailed(),
              OpenSnackbar({
                message:
                  error.status === 404 ? this.systemLiterals.noResultsFound : this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.FlowRecipes:
        return forkJoin({
          recipesData: this.recipesService.searchRecipes(),
          countData: this.recipesService.searchRecipesCount(),
        }).pipe(
          switchMap(({ recipesData, countData }) => {
            return of(GridSuccess({ res: recipesData }), CountChanged({ totalPages: countData.payload.data }));
          }),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.RedeemRules:
        return this.redeemRulesService.searchRedemptionLevels().pipe(
          switchMap((res: any) => {
            const rules = res.redemptionLevelRules || [];
            const gridResponse: any = {
              messages: [{ type: 'success', description: 'OK', code: 200 }],
              payload: { data: rules, filterResults: rules.length },
              success: true,
            };
            return of(GridSuccess({ res: gridResponse }));
          }),
          catchError(() =>
            of(
              GridFailed(),
              OpenSnackbar({
                message: this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      case ActiveComponent.CampaignSearch:
        return this.campaignSearchService.search().pipe(
          switchMap((res: any) => of(GridSuccess({ res }))),
          catchError((error) =>
            of(
              GridFailed(),
              OpenSnackbar({
                message:
                  error.status === 404 ? this.systemLiterals.noResultsFound : this.systemLiterals.systemGridFailed,
                panelClass: 'failed-snackbar',
              }),
            ),
          ),
        );
      default:
        return of(GridFailed());
    }
  }

  dateFormat(date: Date) {
    return format(parseISO(date.toISOString()), "yyyy-MM-dd'T'HH:mm:ss.SSSxx");
  }
}
